"use client";

/**
 * ConfirmDialog — 화면 중앙 확인 다이얼로그 (삭제/이탈 등 위험한 액션 재확인용)
 *
 * 제어 컴포넌트. title/description 아래 취소·확인 버튼이 바로 붙어 나온다.
 * 확인 버튼은 기본으로 프라이머리 색(variant="default")을 쓰고, 필요하면 destructive 로 바꿀 수 있다.
 *
 * 부모 모달이 닫힐 때 같이 닫으려면 open 을 부모의 open 상태와 함께 조합한다:
 * <ConfirmDialog open={confirmOpen && parentOpen} ... />
 *
 * @example
 * const [confirmOpen, setConfirmOpen] = useState(false);
 * <ConfirmDialog
 *   open={confirmOpen}
 *   onClose={() => setConfirmOpen(false)}
 *   title="제휴사를 삭제하시겠습니까?"
 *   description="삭제하면 되돌릴 수 없습니다."
 *   confirmLabel="삭제"
 *   loading={deleteMutation.isPending}
 *   onConfirm={() => deleteMutation.mutate(...)}
 * />
 */
import { type ReactNode } from "react";

import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  /** 다이얼로그 제목 */
  title?: ReactNode;
  /** 제목 아래 보조 설명 */
  description?: ReactNode;
  /** 확인 버튼 라벨 (기본 "확인") */
  confirmLabel?: string;
  /** 취소 버튼 라벨 (기본 "취소") */
  cancelLabel?: string;
  /** 확인 버튼 클릭 시 실행 */
  onConfirm: () => void;
  /** 확인 버튼 색상 (기본 "default" = 프라이머리 색) */
  confirmVariant?: "default" | "destructive";
  /** 확인 처리 중 여부 — 스피너 표시 + 처리 중엔 닫기(오버레이/ESC/취소) 막기 */
  loading?: boolean;
  className?: string;
}

export function ConfirmDialog({
  open,
  onClose,
  title,
  description,
  confirmLabel = "확인",
  cancelLabel = "취소",
  onConfirm,
  confirmVariant = "default",
  loading = false,
  className,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !loading) onClose();
      }}
    >
      <DialogContent
        showClose={false}
        className={className ?? "max-w-sm"}
        onInteractOutside={loading ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={loading ? (e) => e.preventDefault() : undefined}
      >
        {(title || description) && (
          <DialogHeader>
            {title && <DialogTitle>{title}</DialogTitle>}
            {description && (
              <DialogDescription>{description}</DialogDescription>
            )}
          </DialogHeader>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={confirmVariant} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
